import { FC, ReactNode } from 'react'

import { MUIButton } from 'components/common/MUIButton'
import { PostEditorError } from './usePostEditor'
import { SystemMessage } from 'components/common/SystemMessage'
import styles from './CampaignPostEditor.module.sass'
import { useTranslation } from 'react-i18next'

interface Props {
  /** What the backend refused or failed on the post shown */
  error: PostEditorError | null
  /** Why a change cannot be saved, once it held an action back */
  heldBack: string | null
  /** "Discard changes": offered while the form holds a change of a saved post */
  onDiscard?: () => void
}

/** The alert above the editor's form: the backend's refusal, or a change the editor could not save. */
export const PostEditorAlert: FC<Props> = ({ error, heldBack, onDiscard }) => {
  const { t } = useTranslation(['social_campaign'])

  let message: { title?: string, body: ReactNode } | null = null
  if (heldBack) {
    message = { body: t('editor.error.held_back', { reason: heldBack }) }
  } else if (error?.kind === 'not_publishable') {
    message = {
      title: t('flash.not_publishable', { count: error.posts.length }),
      body: (
        <ul className={styles.posts}>
          {error.posts.map((item, index) => (
            <li key={index}>{t('flash.not_publishable_post', { subject: item.subject, reasons: item.reasons.map((reason) => t(`flash.reason.${reason}`)).join(', ') })}</li>
          ))}
        </ul>
      ),
    }
  } else if (error?.kind === 'conflict') {
    message = { body: t('editor.error.conflict') }
  } else if (error?.kind === 'text_refused') {
    message = { body: t('editor.error.text_refused') }
  } else if (error?.kind === 'failed') {
    message = { body: t('flash.error') }
  }

  return (
    <>
      {message &&
        <SystemMessage
          variant="error"
          role="alert"
          title={message.title}
          message={message.body}
          isRawContent
          actions={onDiscard && <MUIButton type="secondaryBorder" size="sm" onClick={onDiscard}>{t('editor.discard')}</MUIButton>}
        />
      }
    </>
  )
}
